import React, { Component } from 'react';
import Icon from '../../../../icons/Icon';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import CustomAnimateButton from '../../../custom/CustomAnimateButton';
import ActionService from '../../../../service/ActionService';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import { ActiveRiskType, TransactionData } from '../../../../domain/stream/transaction/TransactionDomains';
import { CaseAction, CaseActionStatus } from '../../../../domain/case/CaseDomains';
import Editor from '@monaco-editor/react';
import FormicaLabel from '../../../custom/FormicaLabel';
import MessageUtil from '../../../../util/MessageUtil';
import LanguageUtil from '../../../../util/LanguageUtil';
import { assertCanPerformAction, hasCasesActionPermission, notifyActionNotPermitted } from '../../../../util/CaseBulkCloseUtil';

class CaseActionModalComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly actionService: ActionService = ServiceContainer.getInstance().get(ServiceNames.ACTION);

  public render() {
    const { actionType, loading, response } = this.state;

    return (
      <div className={'flex flex-col w-full h-full overflow-y-scroll py-5 px-5'}>
        <div className={'flex flex-col gap-y-5 justify-start items-center h-full'}>
          <div className={'flex flex-col gap-x-1 w-full'}>
            <FormicaLabel label={'Action'} />
            <FormicaSearchSelectBox
              options={this.getActionOptions()}
              value={actionType}
              onChange={(e: FormicaSelectBoxItem) => this.setState({ actionType: e.value, response: undefined })}
            />
          </div>
          {response ? (
            <Editor options={{ readOnly: true }} className={'max-h-[200px] h-[200px]'} language='json' value={response} />
          ) : !actionType ? (
            'Please select action'
          ) : (
            <CustomAnimateButton
              disabled={loading || !hasCasesActionPermission()}
              loading={loading}
              customSize={'md'}
              icon={<Icon icon={'deploy-with-bg'} size={40} />}
              text={LanguageUtil.get({ id: 'common.label.send', defaultMessage: 'Send' })}
              onClick={this.sendAction}
            />
          )}
        </div>
      </div>
    );
  }

  private sendAction = async () => {
    const { actionType } = this.state;
    const { transactionData, onChangeActionRequiredCheck, onChangeActionErrorCheck } = this.props;
    if (!assertCanPerformAction()) {
      return;
    }
    if (actionType && transactionData) {
      try {
        this.setState({ loading: true });
        const transactionForAction = this.createTransactionForAction(transactionData);
        const response = await this.actionService.sendAction(actionType, transactionForAction);
        if (response) {
          this.setState({ response: JSON.stringify(response, null, 2), loading: false });
          onChangeActionRequiredCheck(true);
          if (response.error) {
            onChangeActionErrorCheck(true);
            MessageUtil.error({
              summary: 'Send Action',
              detail: <div>Action could not be sent, error was received</div>,
            });
          }
        }
      } catch (e) {
        const isUnauthorizedResponse: boolean =
          typeof e === 'object' && e != null && 'response' in e
          && (e as { response?: { status?: number } }).response?.status === 401;
        if (isUnauthorizedResponse) {
          notifyActionNotPermitted();
        }
        console.error(e);
      }
    }
  };

  private createTransactionForAction = (transactionData: TransactionData): TransactionData => {
    const { reason, riskType } = this.props;
    if (!reason || reason.trim().length === 0) {
      return transactionData;
    }

    const metadata = {
      ...(transactionData.result[riskType].metadata || {}),
      code: reason,
    };

    return {
      ...transactionData,
      result: {
        ...transactionData.result,
        [riskType]: {
          ...transactionData.result[riskType],
          metadata,
        },
      },
    };
  };

  private getActionOptions = (): FormicaSelectBoxItem[] => {
    const { actions, closeType } = this.props;
    if (actions == undefined || !Array.isArray(actions)) return [];
    const validActions = actions.filter((action) => action && action.name && typeof action.name === 'string' && action.name.trim().length > 0);
    
    if (closeType === 'ON_HOLD') {
      return validActions
        .filter((action) => (action.status || CaseActionStatus.ALL) === CaseActionStatus.ALL)
        .map((action) => ({ label: action.name, value: action.name }));
    }

    const targetStatuses: CaseActionStatus[] =
      closeType === 'FRAUD' ? [CaseActionStatus.ALL, CaseActionStatus.FRAUD] : [CaseActionStatus.ALL, CaseActionStatus.NOT_FRAUD];

    return validActions
      .filter((action) => targetStatuses.includes(action.status || CaseActionStatus.ALL))
      .map((action) => ({ label: action.name, value: action.name }));
  };
}

interface Props {
  actions: CaseAction[] | undefined;
  transactionData: TransactionData | undefined;
  reason: string | undefined;
  riskType: ActiveRiskType;
  onChangeActionRequiredCheck: (value: boolean) => void;
  onChangeActionErrorCheck: (value: boolean) => void;
  closeType: 'FRAUD' | 'NOT_FRAUD' | 'ON_HOLD';
}

class State {
  actionType: string | undefined;
  response: any;
  loading: boolean = false;
}

export default CaseActionModalComponent;
